import { useState, useRef } from 'react';
import { ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const SUBJECT_OPTIONS = [
  { value: 'bug', label: 'Signaler un bug' },
  { value: 'suggestion', label: 'Suggestion d\'amélioration' },
  { value: 'question', label: 'Question' },
  { value: 'autre', label: 'Autre' },
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

function validate(data: { name: string; email: string; subject: string; message: string }): FormErrors {
  const errors: FormErrors = {};
  if (!data.name.trim()) errors.name = 'Le nom est requis';
  if (!data.email.trim()) errors.email = 'L\'email est requis';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = 'Email invalide';
  if (!data.subject) errors.subject = 'Le sujet est requis';
  if (!data.message.trim()) errors.message = 'Le message est requis';
  else if (data.message.length > 5000) errors.message = 'Le message ne doit pas dépasser 5000 caractères';
  return errors;
}

export function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Honeypot field ref
  const honeypotRef = useRef('');
  // Timestamp au chargement
  const loadTimeRef = useRef(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    const formData = { name, email, subject, message };
    const validationErrors = validate(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          website: honeypotRef.current,
          _t: loadTimeRef.current,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          setServerError('Trop de messages envoyés. Veuillez patienter avant de réessayer.');
        } else {
          const data = await response.json().catch(() => null);
          setServerError(data?.message || 'Une erreur est survenue. Veuillez réessayer.');
        }
        return;
      }

      setIsSuccess(true);
    } catch {
      setServerError('Impossible de contacter le serveur. Vérifiez votre connexion.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-ink)' }}
      >
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: 'var(--color-accent)', opacity: 0.1 }}
            >
              <CheckCircle2
                className="w-8 h-8"
                style={{ color: 'var(--color-accent)' }}
              />
            </div>
            <h1
              className="text-2xl font-semibold mb-3"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Message envoyé
            </h1>
            <p className="mb-8" style={{ color: 'var(--color-text-secondary)' }}>
              Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
              Une confirmation a été envoyée à votre adresse email.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all hover:brightness-110"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-inverse)',
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-ink)' }}
    >
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Accueil
          </Link>
          <span style={{ color: 'var(--color-border)' }}>|</span>
          <span
            className="text-sm font-medium"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            BPA Ecosystem
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-2xl mx-auto px-6 py-12 w-full">
        <h1
          className="text-3xl font-semibold mb-2"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Contactez-nous
        </h1>
        <p className="mb-8" style={{ color: 'var(--color-text-secondary)' }}>
          Un problème, une question ou une suggestion ? Remplissez le formulaire ci-dessous.
        </p>

        {serverError && (
          <div
            className="flex items-center gap-3 p-4 rounded-lg mb-6 border"
            style={{
              backgroundColor: 'hsl(0 84% 60% / 0.08)',
              borderColor: 'hsl(0 84% 60% / 0.2)',
              color: 'hsl(0 84% 40%)',
            }}
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{serverError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot — caché visuellement */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              onChange={(e) => { honeypotRef.current = e.target.value; }}
            />
          </div>

          {/* Nom */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Nom *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2"
              style={{
                borderColor: errors.name ? 'hsl(0 84% 60%)' : 'var(--color-border)',
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-ink)',
              }}
              placeholder="Votre nom"
            />
            {errors.name && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 84% 60%)' }}>{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Email *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2"
              style={{
                borderColor: errors.email ? 'hsl(0 84% 60%)' : 'var(--color-border)',
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-ink)',
              }}
              placeholder="votre@email.com"
            />
            {errors.email && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 84% 60%)' }}>{errors.email}</p>
            )}
          </div>

          {/* Sujet */}
          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Sujet *
            </label>
            <select
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2"
              style={{
                borderColor: errors.subject ? 'hsl(0 84% 60%)' : 'var(--color-border)',
                backgroundColor: 'var(--color-surface)',
                color: subject ? 'var(--color-ink)' : 'var(--color-text-tertiary)',
              }}
            >
              <option value="">Sélectionnez un sujet</option>
              {SUBJECT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.subject && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 84% 60%)' }}>{errors.subject}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Message *
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 resize-vertical"
              style={{
                borderColor: errors.message ? 'hsl(0 84% 60%)' : 'var(--color-border)',
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-ink)',
              }}
              placeholder="Décrivez votre problème ou votre demande..."
            />
            <div className="flex justify-between mt-1">
              {errors.message ? (
                <p className="text-xs" style={{ color: 'hsl(0 84% 60%)' }}>{errors.message}</p>
              ) : (
                <span />
              )}
              <span
                className="text-xs"
                style={{
                  color: message.length > 4500 ? 'hsl(0 84% 60%)' : 'var(--color-text-tertiary)',
                }}
              >
                {message.length}/5000
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-text-inverse)',
            }}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Envoyer le message
              </>
            )}
          </button>
        </form>
      </main>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-2xl mx-auto px-6 py-6 text-center">
          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            BPA Ecosystem
          </p>
        </div>
      </footer>
    </div>
  );
}
